(function (root, factory) {
  const api = factory(root);
  if (typeof module === "object" && module.exports) module.exports = api;
  if (root) root.ArcChillLanguageAssist = api;
  if (root && root.document) {
    const start = () => api.init(root.document);
    if (root.document.readyState === "loading") {
      root.document.addEventListener("DOMContentLoaded", start, { once: true });
    } else {
      start();
    }
  }
})(typeof window !== "undefined" ? window : globalThis, function (root) {
  "use strict";

  const STORAGE_KEY = "arc-language-decision-v1";
  const STAY_TTL_MS = 180 * 24 * 60 * 60 * 1000;
  const COPY = {
    en: {
      message: "Your browser language is English. Would you like to switch to the English version?",
      accept: "Switch to English",
      stay: "Stay in Japanese",
    },
    de: {
      message: "Ihre Browsersprache ist Deutsch. Möchten Sie zur deutschen Version wechseln?",
      accept: "Zu Deutsch wechseln",
      stay: "Auf Japanisch bleiben",
    },
    es: {
      message: "El idioma de su navegador es español. ¿Desea cambiar a la versión en español?",
      accept: "Cambiar a español",
      stay: "Continuar en japonés",
    },
    "zh-CN": {
      message: "检测到您的浏览器语言为简体中文，是否切换至简体中文版？",
      accept: "切换至简体中文",
      stay: "继续使用日语",
    },
  };

  function normalizeBrowserLocale(value) {
    const locale = String(value || "").trim().toLowerCase();
    if (/^ja(?:-|$)/.test(locale)) return "ja";
    if (/^en(?:-|$)/.test(locale)) return "en";
    if (/^de(?:-|$)/.test(locale)) return "de";
    if (/^es(?:-|$)/.test(locale)) return "es";
    if (/^zh-(?:hans(?:-|$)|cn(?:-|$)|sg(?:-|$))/.test(locale)) return "zh-CN";
    return null;
  }

  function findSuggestedLocale(browserLanguages, currentLocale) {
    if (normalizeBrowserLocale(currentLocale) !== "ja") return null;
    for (const value of Array.from(browserLanguages || [])) {
      const locale = normalizeBrowserLocale(value);
      if (locale === "ja") return null;
      if (locale && COPY[locale]) return locale;
    }
    return null;
  }

  function createDecision(locale, action, timestamp = Date.now()) {
    return JSON.stringify({ locale, action, timestamp });
  }

  function parseDecision(raw, now = Date.now()) {
    if (!raw) return null;
    try {
      const value = JSON.parse(raw);
      if (!value || !["stay", "selected"].includes(value.action)) return null;
      if (!normalizeBrowserLocale(value.locale)) return null;
      if (!Number.isFinite(value.timestamp)) return null;
      if (value.action === "stay" && now - value.timestamp > STAY_TTL_MS) return null;
      return { locale: value.locale, action: value.action, timestamp: value.timestamp };
    } catch (_error) {
      return null;
    }
  }

  function shouldSuggest({ currentLocale, browserLanguages, storedDecision, now = Date.now(), designMode = false }) {
    if (designMode || parseDecision(storedDecision, now)) return null;
    return findSuggestedLocale(browserLanguages, currentLocale);
  }

  function readDecision(storage) {
    try {
      return storage ? storage.getItem(STORAGE_KEY) : null;
    } catch (_error) {
      return null;
    }
  }

  function writeDecision(storage, locale, action) {
    try {
      if (storage) storage.setItem(STORAGE_KEY, createDecision(locale, action));
    } catch (_error) {}
  }

  function submitLocale(form, locale, storage, action = "selected") {
    const field = form.querySelector("[data-arc-locale-field]");
    if (!field) return;
    field.disabled = false;
    field.value = locale;
    writeDecision(storage, locale, action);
    if (typeof form.requestSubmit === "function") form.requestSubmit();
    else form.submit();
  }

  function buildSuggestion(document, target, form, storage) {
    const copy = COPY[target];
    const card = document.createElement("aside");
    card.className = "arc-language-suggestion";
    card.setAttribute("role", "region");
    card.setAttribute("aria-live", "polite");
    card.innerHTML = `
      <div class="arc-language-suggestion__icon" aria-hidden="true">
        <svg viewBox="0 0 24 24" focusable="false"><circle cx="12" cy="12" r="9"></circle><path d="M3 12h18M12 3c3 3.2 3 14.8 0 18M12 3c-3 3.2-3 14.8 0 18"></path></svg>
      </div>
      <p>${copy.message}</p>
      <div class="arc-language-suggestion__actions">
        <button type="button" class="arc-language-suggestion__accept">${copy.accept}</button>
        <button type="button" class="arc-language-suggestion__stay">${copy.stay}</button>
      </div>`;
    card.querySelector(".arc-language-suggestion__accept").addEventListener("click", () => {
      submitLocale(form, target, storage);
    });
    card.querySelector(".arc-language-suggestion__stay").addEventListener("click", () => {
      writeDecision(storage, "ja", "stay");
      card.remove();
    });
    return card;
  }

  function init(document) {
    const shell = document.querySelector("[data-arc-language-assist]");
    const form = shell?.querySelector("form");
    if (!shell || !form || shell.dataset.arcLanguageReady === "true") return;
    shell.dataset.arcLanguageReady = "true";

    let storage = null;
    try {
      storage = root.localStorage;
    } catch (_error) {}

    const trigger = shell.querySelector("[data-arc-language-trigger]");
    const panel = shell.querySelector("[data-arc-language-panel]");
    const closePanel = () => {
      if (!panel || !trigger) return;
      panel.hidden = true;
      trigger.setAttribute("aria-expanded", "false");
    };

    if (trigger && panel) {
      trigger.addEventListener("click", () => {
        const open = panel.hidden;
        panel.hidden = !open;
        trigger.setAttribute("aria-expanded", String(open));
      });
      document.addEventListener("click", (event) => {
        if (!shell.contains(event.target)) closePanel();
      });
      document.addEventListener("keydown", (event) => {
        if (event.key === "Escape") closePanel();
      });
    }

    shell.querySelectorAll("[data-arc-locale-option]").forEach((button) => {
      button.addEventListener("click", () => {
        submitLocale(form, button.dataset.arcLocaleOption, storage);
      });
    });

    const browserLanguages = root.navigator?.languages || [root.navigator?.language];
    const target = shouldSuggest({
      currentLocale: shell.dataset.currentLocale,
      browserLanguages,
      storedDecision: readDecision(storage),
      designMode: Boolean(root.Shopify?.designMode),
    });
    const targetAvailable = Array.from(shell.querySelectorAll("[data-arc-locale-option]"))
      .some((button) => button.dataset.arcLocaleOption === target);
    if (!target || !targetAvailable) return;

    root.setTimeout(() => {
      if (!document.querySelector(".arc-language-suggestion")) {
        document.body.appendChild(buildSuggestion(document, target, form, storage));
      }
    }, 700);
  }

  return {
    STORAGE_KEY,
    normalizeBrowserLocale,
    findSuggestedLocale,
    createDecision,
    parseDecision,
    shouldSuggest,
    init,
  };
});
